import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  Building2, 
  Lock,
  ArrowRight,
  Check,
  User,
  Loader2,
  Info,
  AlertCircle,
  KeyRound,
  ArrowLeft
} from "lucide-react";

const BusinessLogin = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [nickname, setNickname] = useState("");
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [nicknameStatus, setNicknameStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [showPinInput, setShowPinInput] = useState(false);
  const [bannedMessage, setBannedMessage] = useState(null);
  const [needsPinSetup, setNeedsPinSetup] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const [pinSetupStep, setPinSetupStep] = useState(1);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const storedToken = localStorage.getItem('session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        const response = await fetch(`${API}/auth/me`, { 
          credentials: "include",
          headers 
        });
        if (response.ok) {
          const userData = await response.json();
          if (userData.user_category === "business") {
            login(userData, storedToken);
            navigate("/business");
          }
        }
      } catch (error) {
        console.log("No active session");
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    if (nickname.length < 2) {
      setNicknameStatus(null);
      setShowPinInput(false);
      setBannedMessage(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingNickname(true);
      try {
        const response = await fetch(`${API}/auth/check-nickname/${encodeURIComponent(nickname)}`);
        const data = await response.json();
        setNicknameStatus(data);
        
        if (data.is_banned) {
          setBannedMessage("Esta cuenta ha sido suspendida. Contacta al administrador: hola@okgo.mx");
          setShowPinInput(false);
        } else if (data.exists && data.can_login) {
          if (data.user_category !== "business") {
            setBannedMessage("Esta cuenta no es de tipo Negocio. Usa el acceso correspondiente.");
            setShowPinInput(false);
          } else {
            setShowPinInput(true);
            if (data.needs_pin_setup) {
              setNeedsPinSetup(true);
            }
          }
        } else if (!data.exists) {
          setBannedMessage(null);
          setShowPinInput(false);
        } else {
          setShowPinInput(false);
        }
      } catch (error) {
        console.error("Error checking nickname:", error);
      } finally {
        setCheckingNickname(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [nickname]);

  const handleAuth = async () => {
    if (!nickname || nickname.length < 2) {
      toast.error("Ingresa un nickname válido (mínimo 2 caracteres)");
      return;
    }
    
    if (!pin || pin.length !== 4) {
      toast.error("Ingresa un PIN de 4 dígitos");
      return;
    }

    const nicknameToUse = nicknameStatus?.correct_nickname || nickname.trim().toLowerCase();

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/nickname`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ nickname: nicknameToUse, pin }),
      });

      if (response.ok) {
        const userData = await response.json();
        
        if (userData.user_category !== "business") {
          toast.error("Esta cuenta no es de tipo Negocio");
          return;
        }
        
        if (userData.needs_pin_setup) {
          setTempUserData(userData);
          setNeedsPinSetup(true);
          setPinSetupStep(1);
          setPin("");
          toast.info("Debes configurar tu PIN personal");
        } else {
          login(userData, userData.session_token);
          toast.success(`¡Bienvenido, ${userData.name}!`);
          navigate("/business");
        }
      } else {
        const error = await response.json();
        if (response.status === 403) {
          setBannedMessage(error.detail);
          toast.error("Cuenta suspendida");
        } else if (response.status === 404) {
          toast.error("Usuario no encontrado. Regístrate en okgo.");
        } else {
          toast.error(error.detail || "Error al iniciar sesión");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handlePinSetup = async () => {
    if (newPin.length !== 4) {
      toast.error("El PIN debe ser de 4 dígitos");
      return;
    }
    
    if (newPin !== confirmPin) {
      toast.error("Los PINs no coinciden");
      setConfirmPin("");
      return;
    }
    
    if (newPin === "9999") {
      toast.error("No puedes usar el PIN inicial. Elige otro.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/user/pin`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${tempUserData.session_token}`
        },
        credentials: "include",
        body: JSON.stringify({ pin: newPin }),
      });

      if (response.ok) {
        toast.success("¡PIN configurado exitosamente!");
        login(tempUserData, tempUserData.session_token);
        navigate("/business");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al configurar PIN");
      }
    } catch (error) {
      console.error("PIN setup error:", error);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  // PIN Setup Screen
  if (needsPinSetup && tempUserData) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-green-500/10 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-green-600/10 rounded-full blur-[100px] animate-pulse delay-1000" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
              <KeyRound className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-3xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                Configura tu PIN
              </span>
            </div>
          </div>

          <div className="w-full max-w-sm">
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white mb-2">¡Hola, {tempUserData.name}!</h2>
                <p className="text-slate-400 text-sm">
                  Es tu primer inicio de sesión. Por seguridad, debes crear tu propio PIN de 4 dígitos.
                </p>
              </div>

              <div className="flex items-center justify-center gap-3 mb-6">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  pinSetupStep === 1 ? 'bg-green-600 text-white' : 'bg-green-600 text-white'
                }`}>
                  {pinSetupStep === 1 ? '1' : '✓'}
                </div>
                <div className="w-8 h-0.5 bg-slate-700" />
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  pinSetupStep === 2 ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-400'
                }`}>
                  2
                </div>
              </div>

              {pinSetupStep === 1 ? (
                <>
                  <p className="text-center text-slate-300 mb-4">Ingresa tu nuevo PIN</p>
                  
                  <div className="flex justify-center gap-3 mb-6">
                    {[0, 1, 2, 3].map((i) => (
                      <div 
                        key={i} 
                        className={`w-4 h-4 rounded-full transition-all duration-200 ${
                          i < newPin.length ? 'bg-green-500 scale-110' : 'bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => {
                          if (newPin.length < 4) {
                            setNewPin(newPin + num);
                          }
                        }}
                        className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-2xl font-medium transition-all duration-150 border border-slate-700"
                      >
                        {num}
                      </button>
                    ))}
                    <div></div>
                    <button
                      type="button"
                      onClick={() => {
                        if (newPin.length < 4) {
                          setNewPin(newPin + '0');
                        }
                      }}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-2xl font-medium transition-all duration-150 border border-slate-700"
                    >
                      0
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewPin(newPin.slice(0, -1))}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-lg font-medium transition-all duration-150 border border-slate-700"
                    >
                      ⌫
                    </button>
                  </div>

                  {newPin.length === 4 && (
                    <Button
                      onClick={() => setPinSetupStep(2)}
                      className="w-full h-12 mt-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-xl font-semibold"
                    >
                      Continuar
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-center text-slate-300 mb-4">Confirma tu nuevo PIN</p>
                  
                  <div className="flex justify-center gap-3 mb-6">
                    {[0, 1, 2, 3].map((i) => (
                      <div 
                        key={i} 
                        className={`w-4 h-4 rounded-full transition-all duration-200 ${
                          i < confirmPin.length ? 'bg-green-500 scale-110' : 'bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => {
                          if (confirmPin.length < 4) {
                            setConfirmPin(confirmPin + num);
                          }
                        }}
                        className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-2xl font-medium transition-all duration-150 border border-slate-700"
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setNewPin("");
                        setConfirmPin("");
                        setPinSetupStep(1);
                      }}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 text-sm font-medium transition-all duration-150 border border-slate-700"
                    >
                      Atrás
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirmPin.length < 4) {
                          setConfirmPin(confirmPin + '0');
                        }
                      }}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-2xl font-medium transition-all duration-150 border border-slate-700"
                    >
                      0
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmPin(confirmPin.slice(0, -1))}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-lg font-medium transition-all duration-150 border border-slate-700"
                    >
                      ⌫
                    </button>
                  </div>

                  {confirmPin.length === 4 && (
                    <Button
                      onClick={handlePinSetup}
                      disabled={loading}
                      className="w-full h-12 mt-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-xl font-semibold"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Confirmar PIN
                          <Check className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-green-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-green-600/10 rounded-full blur-[100px] animate-pulse delay-1000" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 relative z-10">
        <div className="flex flex-col items-center mb-8">
          <img src="/okchat-logo.png" alt="OkChat" className="h-32 w-auto drop-shadow-lg" />
          <span className="text-xs text-green-400 mt-1 font-medium tracking-wider uppercase">Negocio</span>
        </div>

        <div className="w-full max-w-sm">
          <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 shadow-2xl shadow-green-500/5">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Acceso Negocio</h2>
              <p className="text-slate-400 text-sm">Ingresa tu Nickname/OkUser de negocio</p>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                <p className="text-xs text-green-300">
                  <strong>Nuevo negocio:</strong> Si tu cuenta fue creada en okgo, 
                  ingresa con el PIN temporal <strong className="text-white">9999</strong> y luego configura tu PIN personal.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="text"
                  placeholder="Nickname/OkUser..."
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full h-12 pl-12 bg-slate-900/50 border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500 focus:ring-green-500/20"
                  data-testid="nickname-input"
                />
                {checkingNickname && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 animate-spin" />
                )}
              </div>

              {nicknameStatus && !bannedMessage && (
                <div className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg ${
                  nicknameStatus.exists && nicknameStatus.can_login && nicknameStatus.user_category === "business"
                    ? 'bg-emerald-500/10 text-emerald-400' 
                    : !nicknameStatus.exists
                      ? 'bg-amber-500/10 text-amber-400'
                      : 'bg-amber-500/10 text-amber-400'
                }`}>
                  {nicknameStatus.exists && nicknameStatus.can_login && nicknameStatus.user_category === "business" ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>
                        ¡Bienvenido de nuevo! 
                        {nicknameStatus.needs_pin_setup && " (PIN temporal detectado)"}
                      </span>
                    </>
                  ) : !nicknameStatus.exists ? (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      <span>Usuario no encontrado. Regístrate en okgo.</span>
                    </>
                  ) : (
                    <span>{nicknameStatus.message}</span>
                  )}
                </div>
              )}

              {bannedMessage && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {bannedMessage}
                </div>
              )}

              {showPinInput && !bannedMessage && (
                <div className="animate-in slide-in-from-top-2 duration-300">
                  <div className="flex justify-center gap-3 mb-6">
                    {[0, 1, 2, 3].map((i) => (
                      <div 
                        key={i} 
                        className={`w-4 h-4 rounded-full transition-all duration-200 ${
                          i < pin.length ? 'bg-green-500 scale-110' : 'bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => {
                          if (pin.length < 4) {
                            setPin(pin + num);
                          }
                        }}
                        className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-2xl font-medium transition-all duration-150 border border-slate-700"
                      >
                        {num}
                      </button>
                    ))}
                    <div></div>
                    <button
                      type="button"
                      onClick={() => {
                        if (pin.length < 4) {
                          setPin(pin + '0');
                        }
                      }}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-2xl font-medium transition-all duration-150 border border-slate-700"
                    >
                      0
                    </button>
                    <button
                      type="button"
                      onClick={() => setPin(pin.slice(0, -1))}
                      className="h-14 rounded-xl bg-slate-800/80 hover:bg-slate-700 active:bg-green-600 text-white text-lg font-medium transition-all duration-150 border border-slate-700 flex items-center justify-center"
                    >
                      ⌫
                    </button>
                  </div>

                  <p className="text-center text-slate-500 text-xs mt-4">
                    {nicknameStatus?.needs_pin_setup 
                      ? "Ingresa el PIN temporal: 9999" 
                      : "Ingresa tu PIN de 4 dígitos"
                    }
                  </p>
                </div>
              )}

              {showPinInput && !bannedMessage && (
                <Button
                  onClick={handleAuth}
                  disabled={loading || pin.length !== 4}
                  className="w-full h-12 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-xl font-semibold shadow-lg shadow-green-600/20 disabled:opacity-50"
                  data-testid="login-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Ingresar
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          <div className="text-center mt-6">
            <Link 
              to="/" 
              className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessLogin;
